/**
 * MCP tools for workflow run results.
 *
 * Mirrors `canary workflow runs list`, `get`, and `diagnostics`.
 *
 * @module
 */

import { mcpApiRequest } from '../api-client';
import { toolJson } from '../tool-helpers';

export function getWorkflowRunsToolDefinitions() {
  return [
    {
      name: 'canary_workflow_runs_list',
      description:
        'List runs for a specific workflow with status, duration, and pagination.',
      inputSchema: {
        type: 'object',
        properties: {
          workflowId: {
            type: 'string',
            description: 'The workflow UUID',
          },
          page: {
            type: 'number',
            description: 'Page number (default: 1)',
          },
          pageSize: {
            type: 'number',
            description: 'Page size (default: 10)',
          },
          mode: {
            type: 'string',
            enum: ['browser', 'api'],
            description: 'Filter by run mode',
          },
        },
        required: ['workflowId'],
      },
    },
    {
      name: 'canary_workflow_runs_get',
      description:
        'Get workflow run detail including steps, status, duration, and error messages. Also serves as a status check for in-progress runs.',
      inputSchema: {
        type: 'object',
        properties: {
          workflowId: {
            type: 'string',
            description: 'The workflow UUID',
          },
          runId: {
            type: 'string',
            description: 'The workflow run UUID',
          },
        },
        required: ['workflowId', 'runId'],
      },
    },
    {
      name: 'canary_workflow_runs_diagnostics',
      description:
        'Get failure diagnostic for a workflow run. Returns AI-generated analysis if available, otherwise falls back to console logs and network requests.',
      inputSchema: {
        type: 'object',
        properties: {
          workflowId: {
            type: 'string',
            description: 'The workflow UUID',
          },
          runId: {
            type: 'string',
            description: 'The workflow run UUID',
          },
        },
        required: ['workflowId', 'runId'],
      },
    },
  ];
}

export async function handleWorkflowRunsTool(
  tool: string,
  args: Record<string, unknown>
) {
  if (tool === 'canary_workflow_runs_list') {
    try {
      const workflowId = args.workflowId as string;
      const params = new URLSearchParams();
      params.set('page', String(args.page ?? 1));
      params.set('pageSize', String(args.pageSize ?? 10));
      if (args.mode) params.set('mode', String(args.mode));

      const result = await mcpApiRequest<{
        ok: boolean;
        error?: string;
        data: { runs: unknown[] };
        pagination: unknown;
      }>('GET', `/workflows/${workflowId}/runs?${params}`);

      if (!result.ok) {
        return toolJson({ ok: false, error: result.error ?? 'Unknown error' });
      }

      return toolJson({ ok: true, runs: result.data.runs, pagination: result.pagination });
    } catch (err) {
      return toolJson({ ok: false, error: err instanceof Error ? err.message : String(err) });
    }
  }

  if (tool === 'canary_workflow_runs_get') {
    try {
      const workflowId = args.workflowId as string;
      const runId = args.runId as string;

      const result = await mcpApiRequest<{
        ok: boolean;
        error?: string;
        data: unknown;
      }>('GET', `/workflows/${workflowId}/runs/${runId}`);

      if (!result.ok) {
        return toolJson({ ok: false, error: result.error ?? 'Unknown error' });
      }

      return toolJson({ ok: true, data: result.data });
    } catch (err) {
      return toolJson({ ok: false, error: err instanceof Error ? err.message : String(err) });
    }
  }

  if (tool === 'canary_workflow_runs_diagnostics') {
    try {
      const workflowId = args.workflowId as string;
      const runId = args.runId as string;

      // Try AI failure diagnostic first
      const result = await mcpApiRequest<{
        ok: boolean;
        error?: string;
        data: unknown;
      }>('GET', `/workflows/${workflowId}/runs/${runId}/failure-diagnostic`);

      if (result.ok && result.data) {
        return toolJson({ ok: true, data: result.data });
      }

      // Fall back to raw diagnostics
      const rawResult = await mcpApiRequest<{
        ok: boolean;
        error?: string;
        consoleLogs: unknown[];
        networkRequests: unknown[];
      }>('GET', `/workflows/${workflowId}/runs/${runId}/diagnostics`);

      if (!rawResult.ok) {
        return toolJson({ ok: true, data: null, message: 'No diagnostic available' });
      }

      return toolJson({
        ok: true,
        consoleLogs: rawResult.consoleLogs,
        networkRequests: rawResult.networkRequests,
      });
    } catch (err) {
      return toolJson({ ok: false, error: err instanceof Error ? err.message : String(err) });
    }
  }

  return null;
}
